import { firstValueFrom, Subject, timeout as timeoutOp, identity } from 'rxjs';
/**
 * A pool of reusable objects.
 * This class manages a pool of objects that can be reused, reducing the overhead of creating and destroying objects frequently.
 * It allows for efficient memory management and performance optimization by recycling objects.
 * @template T The type of objects in the pool.
 */
export class Pool {
    poolConfig;
    #objPool = [];
    #inUse = 0;
    #itemsAdded$ = new Subject();
    loanedObjects = new WeakMap();
    /**
     * @param poolConfig Configuration for the pool.
     */
    constructor(poolConfig) {
        this.poolConfig = poolConfig;
        const { initialSize } = poolConfig;
        if (initialSize) {
            this.grow(initialSize);
        }
    }
    use() {
        if (this.poolConfig.autoGrow !== false &&
            this.#objPool.length === 0 &&
            (!this.poolConfig.maxSize || this.#inUse < this.poolConfig.maxSize)) {
            this.grow(1);
        }
        if (this.#objPool.length === 0) {
            throw new Error('Pool is empty');
        }
        const objToUse = this.#objPool[this.#objPool.length - 1];
        this.#objPool.length--;
        this.#inUse++;
        this.loanedObjects.set(objToUse.value, objToUse);
        return objToUse;
    }
    async useWait(timeout) {
        if (this.#objPool.length > 0) {
            return this.use();
        }
        await firstValueFrom(this.#itemsAdded$.pipe(timeout ? timeoutOp(timeout.toMillis()) : identity));
        return await this.useWait(timeout);
    }
    recycle(obj) {
        if (!(obj instanceof PooledObject)) {
            const found = this.loanedObjects.get(obj);
            if (!found) {
                throw new NotOwnedObjectError(obj, this);
            }
            obj = found;
        }
        // Only recycle if the object is still in the loanedObjects map
        if (!this.loanedObjects.has(obj.value)) {
            return;
        }
        this.#objPool.push(obj);
        this.loanedObjects.delete(obj.value);
        this.#inUse--;
        this.#itemsAdded$.next();
    }
    grow(count = this.#objPool.length + this.#inUse) {
        if (count > 0) {
            const curLen = this.#objPool.length;
            this.#objPool.length += count;
            for (let i = curLen; i < this.#objPool.length; i++) {
                const obj = new PooledObject(this, this.poolConfig.objectFactory());
                this.#objPool[i] = obj;
                this.#itemsAdded$.next();
            }
        }
        return this.#objPool.length;
    }
    shrink(count = 1) {
        for (let i = 0; i < count; i++) {
            if (this.#objPool.length > 0) {
                const removed = this.#objPool[this.#objPool.length - 1];
                this.#objPool.length--;
                if (Symbol.dispose in removed.value) {
                    removed.value[Symbol.dispose]();
                }
            }
        }
        return this.#objPool.length;
    }
    get size() {
        return this.#objPool.length;
    }
}
export class PooledObject {
    #refCount;
    pool;
    value;
    /**
     * Creates a new pooled object.
     * The object is created with a reference count of zero, meaning it is not in use.
     * @param pool The pool that this object belongs to.
     * @param value The value of the pooled object.
     */
    constructor(pool, value) {
        this.#refCount = 0;
        this.value = value;
        this.pool = pool;
    }
    /**
     * The number of references to this pooled object.
     * This is incremented each time `reference()` is called, and decremented each time `dispose()` is called.
     * When the reference count reaches zero, the object is recycled back to the pool.
     */
    get refCount() {
        return this.#refCount;
    }
    /**
     * Takes a reference to this pooled object.
     * This increments the reference count, allowing the object to be reused.
     * This must be used consistently -- either always call `reference()` when using the object,
     * or never call it at all.
     * If you do not call `reference()`, the object will be recycled immediately upon first disposal.
     * If you call `reference()`, you must ensure to call it once for each time you use the object,
     * and then call `dispose()` when you are done with it.
     * @returns this instance, incrementing the reference count.
     */
    reference() {
        this.#refCount++;
        return this;
    }
    /**
     * Disposes of this pooled object.
     * This decrements the reference count, and if it reaches zero, recycles the object back to the pool.
     * If you have called `reference()`, you must call `dispose()` when you are done with the object.
     * @remarks If no references are taken (i.e., `reference()` was never called), this will recycle the object immediately.
     */
    [Symbol.dispose]() {
        this.#refCount--;
        if (this.#refCount < 0) {
            this.#refCount = 0; // Reset to zero to prevent negative reference count
            this.pool.recycle(this);
        }
        else {
            setImmediate(() => {
                if (this.#refCount <= 0) {
                    this.#refCount = 0; // Reset to zero to prevent negative reference count
                    this.pool.recycle(this);
                }
            });
        }
    }
    /**
     * Disposes of this pooled object.
     * This decrements the reference count, and if it reaches zero, recycles the object back to the pool.
     * If you have called `reference()`, you must call `dispose()` when you are done with the object.
     * @remarks If no references are taken (i.e., `reference()` was never called), this will recycle the object immediately.
     */
    dispose() {
        this[Symbol.dispose]();
    }
    /**
     * Returns a string representation of the pooled object.
     * @returns A string representation of the pooled object.
     */
    toString() {
        return `PooledObject(${this.value})`;
    }
    /**
     * Converts the pooled object to a JSON representation.
     * @returns The value of the pooled object.
     */
    toJSON() {
        return this.value;
    }
}
export class NotOwnedObjectError extends Error {
    obj;
    pool;
    constructor(obj, pool) {
        super(`Object ${obj} is not owned by this pool`);
        this.obj = obj;
        this.pool = pool;
        this.name = 'NotOwnedObjectError';
    }
}
//# sourceMappingURL=pool.js.map